import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { TrendingUp, ChevronLeft, Globe, Download, Clock } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmt(n, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(0)}`;
  }
}

export default function PayrollOvertimePage() {
  const navigate = useNavigate();
  const { timezone } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const startDate = `${monthStr}-01`;
  const endDate = (() => { const d = new Date(year, month + 1, 0); return `${monthStr}-${String(d.getDate()).padStart(2,'0')}`; })();
  useEffect(() => { load(); }, [monthStr]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payroll-reports/overtime`, {
        params: { start_date: startDate, end_date: endDate }, withCredentials: true
      });
      setData(res.data);
    } catch { toast.error('Failed to load overtime report'); }
    finally { setLoading(false); }
  };

  const rows = data?.overtime_records || data?.records || [];
  const otCost = (r) => r.ot_cost ?? r.total_ot_cost ?? 0;
  const otDays = (r) => r.ot_days ?? r.ot_days_count ?? 0;
  const currency = rows.find(r => r.currency)?.currency || 'USD';
  const totalHours = rows.reduce((a, r) => a + (r.total_ot_hours || 0), 0);
  const totalCost = rows.reduce((a, r) => a + otCost(r), 0);

  const chartData = rows.slice(0, 12).map(r => ({
    name: r.staff_name?.split(' ')[0],
    hours: +(r.total_ot_hours || 0).toFixed(1),
    cost: Math.round(otCost(r)),
  }));

  const trendData = (data?.daily_trend || []).map(d => ({
    day: d.date?.split('-')[2],
    hours: +(d.total_ot_hours || 0).toFixed(1),
  }));

  return (
    <div className="space-y-6" data-testid="payroll-overtime-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Overtime Report</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[now.getFullYear()-1, now.getFullYear()].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Staff with OT', value: rows.length, c:'text-gray-800', bg:'bg-gray-50' },
              { label: 'Total OT Hours', value: `${totalHours.toFixed(1)}h`, c:'text-amber-600', bg:'bg-amber-50' },
              { label: 'Avg OT/Staff', value: rows.length?`${(totalHours/rows.length).toFixed(1)}h`:'0h', c:'text-blue-600', bg:'bg-blue-50' },
              { label: 'Total OT Cost', value: fmt(totalCost, currency), c:'text-violet-600', bg:'bg-violet-50' },
            ].map(({ label, value, c, bg }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className={`pt-4 ${bg} rounded-xl`}>
                  <p className={`text-xl font-bold ${c}`}>{value}</p><p className="text-xs text-gray-500">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {chartData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">OT Hours per Staff</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip formatter={v => `${v}h`} />
                      <Bar dataKey="hours" fill="#f59e0b" name="OT Hours" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {trendData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Daily OT Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip formatter={v => `${v}h`} />
                      <Line type="monotone" dataKey="hours" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="OT Hours" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Overtime Details</CardTitle>
                <Button size="sm" variant="outline" onClick={() => {
                  const csv = ['Staff,Dept,OT Hours,OT Rate,OT Cost,OT Days']
                    .concat(rows.map(r => `${r.staff_name},${r.department||'–'},${(r.total_ot_hours||0).toFixed(1)},${r.ot_rate||0},${otCost(r)},${otDays(r)}`))
                    .join('\n');
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                  a.download = `overtime-${monthStr}.csv`;
                  a.click();
                }}>
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Staff','Dept','Regular Hrs','OT Hours','OT Days','OT Rate','OT Cost'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.staff_name}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{r.department||'–'}</Badge></td>
                      <td className="px-4 py-3 text-gray-600">{(r.regular_hours||0).toFixed(1)}h</td>
                      <td className="px-4 py-3 text-amber-600 font-bold">{(r.total_ot_hours||0).toFixed(1)}h</td>
                      <td className="px-4 py-3">{otDays(r)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {r.ot_rate != null
                          ? `${fmt(r.ot_rate, r.currency || currency)}${r.ot_multiplier ? ` (${r.ot_multiplier}x)` : ''}`
                          : '–'}
                      </td>
                      <td className="px-4 py-3 text-violet-700 font-semibold">{fmt(otCost(r), r.currency || currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
